// src/components/PrivateRoute.jsx
import React, { useMemo } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { useAuth } from "../context/AuthContext";
import { useRoles } from "../context/RolesContext";
import { isOwner as isOwnerHelper } from "../utils/isOwner";

const BYPASS_PATHS = new Set([
  "/setup-hotel",
  "/kyc-upload",
  "/kyc/phone-capture",
  "/kyc-verification",
  "/email-confirmation-pending",
  "/unauthorized",
  "/logout",
]);

const PrivateRoute = ({ ownerOnly = false, sysAdminOnly = false }) => {
  const { authData, loading } = useAuth();
  const { roles } = useRoles();
  const location = useLocation();

  const {
    token,
    role,
    isEmailConfirmed,
    isKycCompleted,
    trialTime,
  } = authData || {};

  const isOwner = useMemo(() => {
    // Prefer your helper; fall back to the string on the token if roles aren't loaded yet
    const helper = isOwnerHelper(authData, roles);
    if (typeof helper === "boolean") return helper;
    return String(role || "").toLowerCase().trim() === "owner";
  }, [authData, roles, role]);

  const isSysAdmin =
    String(role || "").toLowerCase().trim() === "systemadmin";

  // 1) Hydrating?
  if (loading) return <div>Loading...</div>;

  // 2) Not authenticated
  if (!token) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // 3) Email confirmation gate (applies to everyone)
  if (!isEmailConfirmed && location.pathname !== "/email-confirmation-pending") {
    return <Navigate to="/email-confirmation-pending" replace state={{ from: location }} />;
  }

  // 4) Route explicitly requires SysAdmin?
  if (sysAdminOnly && !isSysAdmin) {
    return <Navigate to="/unauthorized" replace state={{ from: location }} />;
  }

  // 5) Route explicitly requires Owner?
  if (ownerOnly && !isOwner) {
    return <Navigate to="/unauthorized" replace state={{ from: location }} />;
  }

  // 6) Owner-only KYC / Trial checks
  if (isOwner) {
    const onBypass = BYPASS_PATHS.has(location.pathname);

    // KYC first
    const onKycPage = location.pathname === "/kyc-upload" || location.pathname === "/kyc/phone-capture" ||  location.pathname === "/kyc-verification";
    if (!isKycCompleted && !onKycPage && location.pathname !== "/email-confirmation-pending") {
      return <Navigate to="/kyc-upload" replace state={{ from: location }} />;
    }

    // Trial next
    if (Number(trialTime) === 0 && !onBypass) {
      return <Navigate to="/setup-hotel" replace state={{ from: location }} />;
    }
  }

  // 7) OK
  return <Outlet />;
};

PrivateRoute.propTypes = {
  /** If true, only Owner can access this subtree */
  ownerOnly: PropTypes.bool,
  /** If true, only System Admin can access this subtree */
  sysAdminOnly: PropTypes.bool,
};

export default PrivateRoute;
